import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { moneyCompact } from "@/lib/format";
import { Trophy, X } from "lucide-react";

// Fire this after recording a win so the celebration shows right away instead of on the next poll.
export const CELEBRATE_EVENT = "gf:celebrate-check";
export const celebrateNow = () => window.dispatchEvent(new Event(CELEBRATE_EVENT));

const COLORS = ["#003E87", "#F4C400", "#059669", "#0284C7", "#FFFFFF", "#E11D48"];
const SHOW_MS = 6000;
const POLL_MS = 8000;

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 80 }, (_, i) => {
    const round = Math.random() > 0.65;
    const size = 6 + Math.random() * 7;
    return {
      left: Math.random() * 100, delay: Math.random() * 0.5, dur: 2.4 + Math.random() * 1.8,
      color: COLORS[i % COLORS.length], w: size, h: round ? size : size * 0.45, round,
      drift: (Math.random() - 0.5) * 220, rot: Math.random() * 360,
    };
  }), []);
  return (
    <div className="gf-confetti-layer" aria-hidden="true" data-testid="win-celebration-confetti">
      {pieces.map((p, i) => (
        <span key={i} className="gf-confetti" style={{
          left: `${p.left}%`, width: p.w, height: p.h, background: p.color,
          borderRadius: p.round ? 9999 : 2, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`,
          "--drift": `${p.drift}px`, "--rot": `${p.rot}deg`,
        }} />
      ))}
    </div>
  );
}

function Banner({ wins, onClose }) {
  const one = wins.length === 1 ? wins[0] : null;
  const total = wins.reduce((s, w) => s + (Number(w.amount) || 0), 0);
  const title = one ? one.bid_name : `${wins.length} bids won`;
  const detail = one
    ? [one.winner_name, one.client_name, one.amount ? moneyCompact(one.amount) : null].filter(Boolean).join(" · ")
    : [wins.slice(0, 3).map((w) => w.bid_name).join(", ") + (wins.length > 3 ? "…" : ""), total ? moneyCompact(total) : null].filter(Boolean).join(" · ");
  return (
    <div className="absolute top-20 inset-x-0 flex justify-center px-4">
      <div className="pointer-events-auto gf-celebrate-pop w-full max-w-md" data-testid="win-celebration" role="status" aria-live="polite">
        <div className="flex items-center gap-4 rounded-2xl bg-[#0B1220] text-white shadow-2xl border border-[#F4C400]/40 pl-4 pr-3 py-3.5">
          <div className="w-11 h-11 rounded-full bg-[#F4C400] text-black flex items-center justify-center shrink-0">
            <Trophy className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F4C400]">{one ? "Bid won" : "Wins coming in"}</div>
            <div className="font-display font-bold text-base leading-tight truncate" data-testid="win-celebration-project">{title}</div>
            <div className="text-xs text-slate-300 truncate mt-0.5" data-testid="win-celebration-detail">{detail}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white p-1 shrink-0" data-testid="win-celebration-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-4 h-0.5 rounded-full bg-[#F4C400] gf-celebrate-timer" style={{ animationDuration: `${SHOW_MS}ms` }} />
      </div>
    </div>
  );
}

// Team-wide win celebration: everyone signed in sees a confetti burst + banner within seconds of a win.
export function WinCelebration() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const key = user?.user_id ? `gf_celebrated_${user.user_id}` : null;
  const busy = useRef(false);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const check = useCallback(async () => {
    if (!key || document.hidden || busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.get("/celebrations/recent", { _retryDisabled: true });
      let seen = [];
      try { seen = JSON.parse(localStorage.getItem(key) || "[]"); } catch { seen = []; }
      const fresh = (data.items || []).filter((c) => !seen.includes(c.celebration_id));
      if (fresh.length) {
        localStorage.setItem(key, JSON.stringify([...seen, ...fresh.map((c) => c.celebration_id)].slice(-100)));
        setQueue((q) => [...q, ...fresh]);
      }
    } catch { /* offline / signed out — try again next tick */ }
    finally { busy.current = false; }
  }, [key]);

  useEffect(() => {
    if (!key) return undefined;
    check();
    const t = setInterval(check, POLL_MS);
    const soon = () => setTimeout(check, 400);
    window.addEventListener(CELEBRATE_EVENT, soon);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(t);
      window.removeEventListener(CELEBRATE_EVENT, soon);
      document.removeEventListener("visibilitychange", check);
    };
  }, [key, check]);

  useEffect(() => {
    if (current || !queue.length) return;
    setCurrent({ id: queue[0].celebration_id, wins: queue });
    setQueue([]);
  }, [queue, current]);

  useEffect(() => {
    if (!current) return undefined;
    const t = setTimeout(() => setCurrent(null), SHOW_MS);
    return () => clearTimeout(t);
  }, [current]);

  if (!current) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-[120]" data-testid="win-celebration-layer">
      {!reduced && <Confetti key={current.id} />}
      <Banner wins={current.wins} onClose={() => setCurrent(null)} />
    </div>
  );
}
